import { useEffect, useState } from 'react';
import { CustomersService } from '../utility/services/customers';
import { BillingService } from '../utility';
import { useDebounce, useDebouncedEffect } from './debounce';

export const CustomerListHook = (freshList = true, checkRateCardAvailability = false) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const [filter, setFilter] = useState({
    limit: 10,
    page: 1,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        ...filter,
        search: filter.search || '',
      };

      if (!params.search) {
        delete params.search;
      }

      if (checkRateCardAvailability) {
        params.checkRateCardAvailability = true;
      }
      const res = await CustomersService.get(params);
      setData(res?.data);
      setTotalData(res?.totalCount);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFilter = useDebounce(filter, 200);

  useDebouncedEffect(
    () => {
      fetchData();
    },
    [refreshKey, freshList, debouncedFilter],
    300,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    pageChanged,
    totalData,
    totalPages,
    refresh,
  };
};

export const CustomerRateHook = id => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await BillingService.getCustomerTempate(id);
      setData(res);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  return { data, loading, error, refresh: fetchData };
};

export const CustomerRateMarginHook = (customerOrganizationId, warehouseId) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        customerOrganizationId,
        warehouseId,
      };
      const res = await BillingService.getMarginRate(params);
      setData(res);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerOrganizationId && warehouseId) {
      fetchData();
    }
  }, [customerOrganizationId, warehouseId]);

  return { data, loading, error, setData };
};

export const CustomerAutoCompleteHook = search => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) {
        params.search = search.trim();
      }
      const res = await CustomersService.get(params);
      setData(res?.data);
      setTotalData(res?.totalCount);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [search]);

  return {
    data,
    error,
    loading,
    totalData,
    totalPages,
  };
};

export const CustomerDetailHook = id => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await BillingService.getCustomerDetail(id);
      setData(res);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  return { data, loading, error, refresh: fetchData };
};

export const CustomerHoldStatusHook = id => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await BillingService.getCustomerHoldStatus(id);
      setData(res);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  return { data, loading, error, refresh: fetchData };
};
